import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, FileSearch, ClipboardCheck, Users, CheckCircle2,
  AlertTriangle, X, BadgePoundSterling, Search, Building2, Sparkles, ScrollText,
  AlertCircle, MessagesSquare, FileCheck2, Compass
} from "lucide-react";
import SEO from "@/components/SEO";
import Testimonials from "@/components/Testimonials";

const HERO_IMG = "/images/home-hero.jpg";
const COMMUNITY = "/images/operators-band.jpg";

const TRUST_CARDS = [
  { i: <ClipboardCheck />, t: "Annual Health Check", d: "Included with membership — an external visual condition review of your park home each year." },
  { i: <FileSearch />, t: "Quote Review", d: "Check if costs and scope are fair before you pay." },
  { i: <ShieldCheck />, t: "Insurance Guidance", d: "Understand what may or may not be covered before making costly decisions." },
  { i: <Users />, t: "Ongoing Support", d: "Practical help when something goes wrong." },
];

const WARRANTY_BAD = [
  "Limited cover",
  "Exclusions apply",
  "Reactive support only",
  "No yearly check on your home",
  "Often unclear when problems arise",
  "May not assist with quotes, scope or insurance decisions",
];
const WARRANTY_GOOD = [
  "Annual External Health Check on your home",
  "Expert practical guidance",
  "Quote and cost review",
  "Insurance-related technical support",
  "Maintenance responsibility advice",
  "Help before expensive mistakes are made",
];

const WRONG_DECISIONS = [
  "Approving unnecessary repairs",
  "Paying for work that may be claimable",
  "Missing valid insurance-related damage",
  "Accepting inflated contractor costs",
  "Using the wrong repair scope",
  "Leaving defects unresolved until they worsen",
];

const CLAIMS_BULLETS = [
  "Identify damage that may need proper technical assessment",
  "Help you understand whether insurance may apply",
  "Reduce the risk of uninsured losses",
  "Support better repair decisions",
  "Help prevent poor contractor scope and inflated costs",
];

const MEMBER_BENEFITS = [
  "Annual External Park Home Health Check",
  "Independent Quote Reviews",
  "Technical Support",
  "Insurance Guidance",
  "Maintenance Responsibility Advice",
  "Reduced rates for additional inspections",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="Park Home Support | Annual Health Check + Quote Review Membership"
        description="An annual support membership for UK park home owners. Includes an external Park Home Health Check each year, independent quote reviews, insurance guidance and maintenance responsibility advice. £99 first year, then £249/year."
        keywords="park home support, park home health check, park home quote review, park home insurance advice, park home membership UK"
        canonical="https://parkhomesupport.co.uk/"
      />
      {/* HERO */}
      <section className="relative grain-overlay overflow-hidden hero-gradient">
        <div className="container-phs py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 reveal">
              <span className="eyebrow">Annual Support · Health Check Included · Independent</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-[#071A33]">
                An Annual Park Home Support Membership — <span className="text-[#064E2E]">With a Yearly External Health Check Built In.</span>
              </h1>
              <p className="mt-6 text-lg text-[#334155] max-w-2xl leading-relaxed">
                Park Home Support is your annual partner for prevention, advice and trusted second opinions. Every membership includes an Annual External Park Home Health Check — an external visual condition review with maintenance guidance — plus quote reviews, insurance guidance, and ongoing technical support all year round.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/quote-review" className="btn-primary" data-testid="hero-quote-review-btn">
                  Upload a Quote for Review <ArrowRight size={18} />
                </Link>
                <Link to="/membership" className="btn-outline" data-testid="hero-join-btn">
                  Join Park Home Support
                </Link>
              </div>

              <p className="mt-8 text-sm text-[#475569] max-w-xl flex items-start gap-2">
                <CheckCircle2 size={16} className="text-[#064E2E] mt-0.5 shrink-0" />
                Built on years of real-world experience in park home repairs, inspections and insurance-related damage.
              </p>
            </div>

            <div className="lg:col-span-5 reveal" style={{ animationDelay: "0.15s" }}>
              <div className="relative">
                <div className="img-plate">
                  <img src={HERO_IMG} alt="Residential park home" className="w-full h-[460px] object-cover" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-[#071A33] text-white rounded-2xl p-6 max-w-[260px] hidden md:block border-t-4 border-[#B88A2A] shadow-2xl z-10">
                  <p className="text-sm font-medium tracking-wide text-[#B88A2A] uppercase tabular-nums">£99</p>
                  <p className="text-2xl font-display mt-1">First Year</p>
                  <p className="text-xs text-white/70 mt-1">Renews at £249/year</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* URGENT STRIP — high-intent capture immediately below hero */}
      <section className="bg-[#E6F4F3] border-y border-[#BFE3E1] py-6" data-testid="urgent-strip">
        <div className="container-phs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 bg-[#0F766E] text-white rounded-xl flex items-center justify-center shrink-0">
              <AlertCircle size={20} />
            </div>
            <div>
              <h3 className="font-display text-xl text-[#071A33] leading-tight">Urgent issue?</h3>
              <p className="text-sm sm:text-base text-[#475569] leading-snug mt-1">
                Act quickly — but not blindly. Getting it wrong can be costly.
              </p>
            </div>
          </div>
          <Link
            to="/park-home-urgent-repairs"
            className="rounded-full bg-[#064E2E] text-white px-6 py-3 font-semibold hover:bg-[#0F766E] transition inline-flex items-center gap-2 shrink-0"
            data-testid="urgent-strip-cta"
          >
            Get Urgent Support <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* HERO TRUST CARDS */}
      <section className="bg-white pt-12 lg:pt-16">
        <div className="container-phs relative z-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {TRUST_CARDS.map((c) => (
              <div key={c.t} className="card-default" data-testid={`hero-trust-${c.t.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
                <div className="w-10 h-10 bg-[#064E2E] text-white rounded-xl flex items-center justify-center">{c.i}</div>
                <h3 className="font-display text-lg mt-4 text-[#071A33]">{c.t}</h3>
                <p className="mt-2 text-sm text-[#334155] leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ALTERNATIVE TO PAPER WARRANTY */}
      <section className="section-pad bg-white">
        <div className="container-phs">
          <div className="max-w-3xl">
            <span className="eyebrow">A Different Approach</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight text-[#071A33]">
              The Alternative to Paper-Based Warranty Schemes.
            </h2>
            <p className="mt-5 text-lg text-[#334155]">
              Many warranty-style schemes offer limited practical help when something actually goes wrong. Park Home Support is different. We focus on real-world advice, contractor quote review, technical guidance and practical solutions.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="comparison">
            <article className="card-default border-[#FCA5A5]/40" data-testid="compare-warranty">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#DC2626]" />
                <span className="eyebrow text-[#DC2626]">Typical Warranty Scheme</span>
              </div>
              <h3 className="font-display text-2xl mt-2 text-[#071A33]">Limited and reactive</h3>
              <ul className="mt-5 space-y-3 text-[15px] text-[#475569]">
                {WARRANTY_BAD.map((x) => (
                  <li key={x} className="flex gap-2.5"><X size={18} className="text-[#DC2626] mt-0.5 shrink-0"/> {x}</li>
                ))}
              </ul>
            </article>

            <article className="card-teal" data-testid="compare-phs">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#FFE7B6]" />
                <span className="text-[#FFE7B6] tracking-[0.22em] uppercase text-xs font-medium">Park Home Support</span>
              </div>
              <h3 className="font-display text-2xl mt-2">Practical, expert, proactive</h3>
              <ul className="mt-5 space-y-3 text-[15px] text-white/90">
                {WARRANTY_GOOD.map((x) => (
                  <li key={x} className="flex gap-2.5"><CheckCircle2 size={18} className="text-[#FFE7B6] mt-0.5 shrink-0"/> {x}</li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ONE WRONG DECISION */}
      <section className="section-pad bg-[#F7F4ED]">
        <div className="container-phs">
          <div className="max-w-3xl">
            <span className="eyebrow">Avoid Costly Mistakes</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight text-[#071A33]">
              One Wrong Decision Can Cost Thousands.
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WRONG_DECISIONS.map((x, i) => (
              <div key={x} className="card-default" data-testid={`wrong-decision-${i}`}>
                <div className="flex gap-3 items-start">
                  <AlertTriangle size={20} className="text-[#B88A2A] mt-0.5 shrink-0"/>
                  <p className="text-[#071A33] font-medium">{x}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-10 font-display text-2xl sm:text-3xl text-[#064E2E] max-w-3xl leading-snug">
            We help stop those mistakes before they happen.
          </p>
          <div className="mt-6 max-w-3xl border-l-4 border-[#B88A2A] bg-white px-6 py-5 rounded-r-xl">
            <p className="text-[#071A33] text-base sm:text-lg leading-relaxed">
              <span className="font-semibold">Once work starts, it becomes much harder to challenge cost, scope or quality.</span>
            </p>
          </div>
          <div className="mt-8">
            <Link to="/quote-review" className="btn-primary" data-testid="decision-cta-btn">
              Get Your Quote Checked <ArrowRight size={18} />
            </Link>
            <p className="text-sm text-[#64748B] mt-3">
              Many users only realise the risk after seeing their report.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — soft proof until 3+ approved reviews exist */}
      <Testimonials />

      {/* BUILT ON REAL EXPERIENCE */}
      <section className="section-pad bg-[#F7F4ED]" data-testid="real-experience-section">
        <div className="container-phs">
          <div className="rounded-3xl bg-white p-8 md:p-12 border border-[#D7E7DF] shadow-sm max-w-4xl mx-auto">
            <span className="eyebrow">Built on Real Experience</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight text-[#071A33]">
              Built on hands-on park home experience.
            </h2>
            <p className="mt-5 text-[#475569] text-lg leading-relaxed">
              Park Home Support is built on hands-on experience dealing with park home repairs, inspections, contractor scopes and insurance-related damage. This isn&apos;t generic advice — it&apos;s based on understanding how these issues actually play out in the real world.
            </p>
          </div>
        </div>
      </section>

      {/* CLAIMS EXPERIENCE */}
      <section className="section-pad bg-white">
        <div className="container-phs">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <span className="eyebrow">Built on Real Experience</span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight text-[#071A33]">
                Built on Insurance Claims Experience.
              </h2>
              <p className="mt-5 text-lg text-[#334155]">
                Our knowledge comes from real-world involvement in park home damage, repairs, inspections and insurance-related claims. We understand how damage is assessed, where claims often fail, and how poor early decisions can create uninsured losses.
              </p>
              <ul className="mt-8 space-y-3">
                {CLAIMS_BULLETS.map((x) => (
                  <li key={x} className="flex gap-2.5 text-[#071A33]">
                    <CheckCircle2 className="text-[#064E2E] mt-0.5" size={18}/>
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="card-default text-center py-7">
                  <Search className="mx-auto text-[#0F766E]" size={28}/>
                  <p className="font-display text-3xl mt-3 text-[#071A33]">Cause</p>
                  <p className="text-xs text-[#475569] mt-1">of damage assessed</p>
                </div>
                <div className="card-default text-center py-7">
                  <ClipboardCheck className="mx-auto text-[#0F766E]" size={28}/>
                  <p className="font-display text-3xl mt-3 text-[#071A33]">Scope</p>
                  <p className="text-xs text-[#475569] mt-1">checked line by line</p>
                </div>
                <div className="card-default text-center py-7">
                  <BadgePoundSterling className="mx-auto text-[#0F766E]" size={28}/>
                  <p className="font-display text-3xl mt-3 text-[#071A33]">Cost</p>
                  <p className="text-xs text-[#475569] mt-1">validated for fairness</p>
                </div>
                <div className="card-default text-center py-7">
                  <ShieldCheck className="mx-auto text-[#0F766E]" size={28}/>
                  <p className="font-display text-3xl mt-3 text-[#071A33]">Insurance</p>
                  <p className="text-xs text-[#475569] mt-1">considerations flagged</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — simple 4-step */}
      <section className="section-pad bg-white">
        <div className="container-phs">
          <div className="max-w-3xl">
            <span className="eyebrow">How It Works</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight text-[#071A33]">
              Four steps, no guesswork.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {[
              { i: <FileSearch />, t: "Submit your quote or issue", d: "Upload what you have. We accept photos, documents, contractor quotes." },
              { i: <ClipboardCheck />, t: "We review it properly", d: "A specialist looks at scope, cost and any insurance relevance." },
              { i: <Compass />, t: "You get clear guidance", d: "A plain-English written verdict with what to do — and what to avoid." },
              { i: <FileCheck2 />, t: "You decide what to do next", d: "Approve, push back, request a proper inspection, or pause. It's your call." },
            ].map((s, i) => (
              <div key={s.t} className="card-soft" data-testid={`how-step-${i+1}`}>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 bg-[#064E2E] text-white rounded-xl flex items-center justify-center">{s.i}</div>
                  <span className="font-display text-3xl text-[#B88A2A] tabular-nums">{i+1}</span>
                </div>
                <h3 className="font-display text-lg mt-4 text-[#071A33] leading-snug">{s.t}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEMBERSHIP PRICING */}
      <section className="section-pad bg-[#F7F4ED]">
        <div className="container-phs">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <span className="eyebrow">Membership Pricing</span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight text-[#071A33]">
                Join for £99 First Year.
              </h2>
              <p className="mt-5 text-lg text-[#334155]">
                Then £249 per year. Includes your <strong>Annual External Park Home Health Check</strong>, quote reviews, technical support, insurance guidance and maintenance responsibility advice. Cancel anytime before renewal.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="card-dark relative overflow-hidden">
                <div className="absolute -right-10 -top-10 w-44 h-44 bg-[#B88A2A]/10 rounded-full blur-2xl" />
                <span className="text-[#B88A2A] tracking-[0.22em] uppercase text-xs font-medium">Park Home Support Membership</span>
                <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                  <span className="price-display text-6xl">
                    <span className="price-currency">£</span>
                    <span className="price-value">99</span>
                  </span>
                  <span className="text-white/70 leading-snug">first year, then £249/year</span>
                </div>
                <ul className="mt-7 grid sm:grid-cols-2 gap-y-2.5 gap-x-5 text-[15px] text-white/90">
                  {MEMBER_BENEFITS.map((x) => (
                    <li key={x} className="flex gap-2.5"><CheckCircle2 size={18} className="text-[#B88A2A] mt-0.5" /> {x}</li>
                  ))}
                </ul>
                <Link to="/membership" className="btn-accent mt-8 w-full sm:w-auto" data-testid="pricing-join-btn">
                  Join Now — Get Support Before You Commit <ArrowRight size={18} />
                </Link>
                <p className="text-xs text-[#FFE7B6] mt-4 italic">
                  Most members recover the cost of the plan on their first issue.
                </p>
                <p className="text-xs text-white/60 mt-3 leading-relaxed max-w-2xl">
                  This is not insurance or a warranty. Work costs and inspections are quoted separately. Cooling-off rights apply unless the service has been used.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OPERATORS */}
      <section className="relative section-pad bg-[#071A33] text-white overflow-hidden">
        <img src={COMMUNITY} alt="Park community" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="container-phs relative">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-[#B88A2A] tracking-[0.22em] uppercase text-xs font-medium">For Park Operators</span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">Reduce complaints, cost drift and maintenance confusion.</h2>
              <p className="mt-5 text-white/80 text-lg max-w-xl">
                Park operators need visibility, control and consistency. Park Home Support provides structured support, issue triage, quote review and resident-facing guidance — across every site.
              </p>
              <Link to="/operators" className="btn-accent mt-8" data-testid="home-operators-cta-btn">
                Discuss Operator Support <Building2 size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {["Centralised support","Quote and scope review","Reduced friction with residents","Better contractor accountability","Better technical evidence","Improved maintenance control"].map((f) => (
                <div key={f} className="border border-white/15 rounded-2xl p-5 text-sm flex items-start gap-2.5 bg-white/5">
                  <Sparkles size={16} className="text-[#B88A2A] mt-0.5" />{f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-pad bg-[#F7F4ED]" data-testid="final-cta-section">
        <div className="container-phs">
          <div className="rounded-3xl bg-white border border-[#D7E7DF] p-8 md:p-14 shadow-sm text-center max-w-4xl mx-auto">
            <span className="eyebrow">Before You Commit</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 text-[#071A33] leading-tight">
              Before You Approve Anything — Check It Properly.
            </h2>
            <p className="mt-5 text-[#475569] text-lg max-w-2xl mx-auto">
              The cost of a quote review is a fraction of the cost of getting the wrong work done.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/quote-review" className="btn-primary" data-testid="final-cta-quote">
                Get Your Quote Checked <ArrowRight size={18} />
              </Link>
              <Link to="/park-home-urgent-repairs" className="btn-outline" data-testid="final-cta-urgent">
                Get Urgent Support
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white border-t border-[#D7E7DF]">
        <div className="container-phs py-10 flex flex-wrap justify-center gap-3">
          <span className="trust-pill"><ClipboardCheck size={14} className="text-[#0F766E]" /> Annual Health Check included</span>
          <span className="trust-pill"><Users size={14} className="text-[#0F766E]" /> Owner-first</span>
          <span className="trust-pill"><ShieldCheck size={14} className="text-[#0F766E]" /> Independent guidance</span>
          <span className="trust-pill"><FileSearch size={14} className="text-[#0F766E]" /> Quote review included</span>
          <span className="trust-pill"><ScrollText size={14} className="text-[#0F766E]" /> Real claims experience</span>
        </div>
      </section>
    </div>
  );
}
